export interface OrderItem {
  itemId: number;
  name: string;
  quantity: number;
  status: "0" | "1" | "2" | "3";
  isSplit?: boolean;
  isCancelled?: boolean;
  completedQuantity?: number;
  modifiers?: { id: string; name: string }[];
  isAddition?: boolean;
  hasAdditions?: boolean;
  billElementItemId: number | null;
  mainBillElementItemId: number | null;
  prepareTime: number | null;
  // Само при добавка, чийто основен продукт е на ДРУГА станция (BE ги праща
  // на „осиротелите" редове): име на основния продукт и на станцията му.
  mainProductName?: string | null;
  mainStationName?: string | null;
  // Ред от обедно меню: обеденият размер, запомнен при поръчката ({език: текст};
  // липсва → размерът на продукта) — както кухненският бон
  lunchMenuId?: number | null;
  lunchUnitLangs?: Record<string, string> | null;
  // Продукт на НЯКОЛКО станции (напр. „Пилешко филе" на Скара + Студена):
  // BE праща реда на всяка от тях и всяка има СВОЙ статус. `stationIds` са
  // станциите на реда (основната първа), `status` в канала на станция S е
  // статусът на нейната част, а `rowStatus` — общият статус на реда. На ред с
  // една станция двете полета са null (initial/refresh) или ги няма.
  stationIds?: number[] | null;
  rowStatus?: "0" | "1" | "2" | "3" | null;
  // Частите на реда по станции (BE 0.0.280) — от тях мониторът на едната
  // станция показва докъде е другата, а шпикерът — кои станции са готови.
  stationStatuses?: StationPart[] | null;
  // Само клиентско: станцията, чийто канал е донесъл реда (попълва се при
  // сливането на станциите в MonitorsContent) — BE не го праща.
  feedStationId?: number;
}

// Ред на няколко станции ли е (виж OrderItem.stationIds). Празен/липсващ или
// с една станция → обикновен ред, поведението е досегашното.
export const isMultiStation = (item: {
  stationIds?: number[] | null;
}): boolean => (item.stationIds?.length ?? 0) > 1;

// Частта на една станция от ред на няколко станции. `tracked: false` =
// станция без кухненски монитор (само принтер): статусът ѝ не се мени от
// никого, затова не се показва като „чака".
export interface StationPart {
  stationId: number;
  status: "0" | "1" | "2" | "3";
  tracked?: boolean;
}

export interface Order {
  billId: number;
  billElementId: number;
  tableName: string;
  oldTableName?: string | null;
  waiterName: string;
  time: string;
  comment?: string;
  elements: OrderItem[];
}

export interface StatusUpdate {
  itemId: number;
  billId: number;
  billElementId: number;
  status: "0" | "1" | "2" | "3";
}

// Самоличността на ЕДНА поръчка на монитора: двойката (billId, billElementId).
// С нея я праща и облакът, и касата (`app/edge/builders/station.py`), с нея се
// адресират `status_update` и `item_cancelled`, и тя оцелява преименуване на
// маса. По-рано екранът я разпознаваше по `${tableName}-${time}` и две доставки
// на една и съща поръчка се сливаха в ЕДНА картичка със СЪБРАНИ количества
// („2 × Бургер" → „4 × Бургер", докладвано на живо 22.09.2026).
// ВАЖНО: един и същ `bill_element` отива на няколко станции със СЪЩИЯ
// `billElementId` — затова сливането между станции остава коректно.
// Кадър без ид-та (стар/непълен payload) пада на стария ключ: по-добре две
// картички, отколкото слети чужди поръчки. Префиксът пази двата ключа да не
// се засекат случайно.
export const orderIdentityKey = (order: {
  billId?: number | null;
  billElementId?: number | null;
  tableName?: string;
  time?: string;
}): string =>
  order.billId != null && order.billElementId != null
    ? `id:${order.billId}-${order.billElementId}`
    : `tt:${order.tableName ?? ""}-${order.time ?? ""}`;

// Една и съща поръчка ли са? Хукът дедупликира по СЪЩИЯ ключ, по който екранът
// слива — иначе в списъка остават два записа, които рендерът после събира.
//
// РАЗЛИКАТА при липсващи ид-та е нарочна (22.09.2026, находка Н8 на червения
// отбор): за СЛИВАНЕ на екрана резервният ключ „маса-час" е най-доброто, което
// имаме, но за ЗАМЕСТВАНЕ в списъка е опасен — два кадъра без ид-та с една маса
// и един и същи часов печат не са доказано една поръчка, а заместването ИЗТРИВА
// редовете на първия. Затова тук отговорът е „не знам, значи не е тя": по-добре
// две картички, отколкото изядена поръчка. Днес е недостижимо (и облакът, и
// касата пращат ид-та), но цената на грешката е поръчка, която не стига до
// готвача.
export const isSameOrder = (
  left: Parameters<typeof orderIdentityKey>[0],
  right: Parameters<typeof orderIdentityKey>[0],
): boolean => {
  const identified =
    left.billId != null &&
    left.billElementId != null &&
    right.billId != null &&
    right.billElementId != null;
  return identified && orderIdentityKey(left) === orderIdentityKey(right);
};

// Уникален ид на реда в бекенда: за основен продукт — bill_element_items.id
// (mainBillElementItemId), за добавка — bill_element_item_additions.id
// (billElementItemId). null при стари/непълни payload-и.
export const getRowId = (item: {
  isAddition?: boolean;
  billElementItemId: number | null;
  mainBillElementItemId: number | null;
}): number | null =>
  item.isAddition ? item.billElementItemId : item.mainBillElementItemId;

// Коментар към артикула идва като „модификатор" със свободен текст вместо
// числово id (виж _parse_modifier_id в BE) — на мониторите се показва наклонено.
export const isCommentModifier = (modifierId: string): boolean =>
  !/^\s*[+-]?\d+\s*$/.test(String(modifierId));

export interface FlatProduct {
  orderKey: string;
  tableName: string;
  oldTableName?: string | null;
  waiterName: string;
  time: string;
  comment?: string;
  productName: string;
  quantity: number;
  status: "0" | "1" | "2" | "3";
  isSplit: boolean;
  isCancelled: boolean;
  isFirstInOrder: boolean;
  productIndex: number;
  itemId: number;
  billId: number;
  billElementId: number;
  // Уникален ид на реда в бекенда (виж getRowId) — за прецизни статус-заявки,
  // когато един и същ продукт е на няколко отделни реда в една поръчка.
  billElementItemId: number | null;
  completedQuantity: number;
  modifiers?: { id: string; name: string }[];
  hasAdditions?: boolean;
  isAddition?: boolean;
  // Контекст за добавка без видим основен продукт на този монитор
  // (основният е на друга станция) — виж OrderItem.
  mainProductName?: string | null;
  mainStationName?: string | null;
  // Част от ред на няколко станции на ОБИКНОВЕН монитор: станцията на тази
  // част (праща се като `stationId` при смяна на статус) и името ѝ за етикета
  // (само когато панелът гледа повече от една станция). null иначе.
  stationId: number | null;
  stationTag: string | null;
  // Подтекст за другите станции на реда: на обикновен монитор „+ Студена
  // кухня · готово ✓" (станциите, които панелът НЕ гледа); на шпикер при
  // „всички статуси" — „Скара ✓ · Студена кухня …". null иначе.
  stationHint: string | null;
  unitAndSize?: string | null;
  // Ред от обедно меню — етикет „Обедно" до името; обеденият размер (ако е сменен)
  isLunch?: boolean;
  lunchUnitAndSize?: string | null;
  prepareTime: number | null;
  isOverdue: boolean;
}

export interface BillSplit {
  oldBill: Order[];
  newBill: Order[];
}

export interface WebSocketMessage {
  type:
    | "initial_data"
    | "new_order"
    | "refresh_data"
    | "status_update"
    | "item_cancelled"
    | "bill_split";
  data: Order | Order[] | StatusUpdate | BillSplit;
}

export const WebSocketStatus = {
  CONNECTING: "connecting",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
} as const;

export type WebSocketStatusType =
  (typeof WebSocketStatus)[keyof typeof WebSocketStatus];
